import { RefObject, useEffect, useRef } from 'react';
import { pointerEngine, PlayGesture } from './pointerEngine';
import { playLoop } from './playLoop';
import { PRESETS, stepSpring, settled, Spring } from './springs';
import { useStorm } from '../storm/StormContext';
import { mayGrab } from './panGate';
import { tiltFor } from './bookTilt';

// Book grab: drag the reveal book by a handle — its spine, the 18px ring band around the page,
// or the cover while closed (elements marked data-book-handle inside the wrapper) — and it
// tilts rigidly in 3D (rotateX/rotateY, clamped ±MAX_TILT). Release springs it level; a
// flick adds a rotateY spin impulse. A horizontal flick left on the closed cover opens it, a
// flick right on the spine while open closes it; a tap on the closed cover opens it. Reduced
// motion: no tilt/spin; tap on the cover opens, tap on the spine closes.
//
// Legibility ruling: the wrapper is an ancestor of the JournalCanvas, which isProtectedElement
// would refuse. Allowed here (and only here) because this is a rigid rotate of the whole book —
// the same class of transform the reveal's scroll-in already applies — never a filter, skew or
// displacement. Grabs only start on handles, which never overlap .gj-page-wrap, so the page's
// own photo drag can't trigger it.
const SPIN_GAIN = 900; // deg/s of rotateY per px/ms of release velocity
const MAX_SPIN = 1400;
const EPS = 0.01;

type Handle = 'ring' | 'spine' | 'cover';
type BookDebug = { rx: number; ry: number; grabbing: boolean; open: boolean; transform: string };
type DebugHost = { __playDebug?: { book?: () => BookDebug | null } };

const clamp = (v: number, m: number) => Math.max(-m, Math.min(m, v));

export function useBookGrab(grabRef: RefObject<HTMLElement>, open: boolean, setOpen: (o: boolean) => void): void {
  const { reducedMotion } = useStorm();
  const openRef = useRef(open);
  const setRef = useRef(setOpen);
  openRef.current = open;
  setRef.current = setOpen;

  useEffect(() => {
    const el = grabRef.current;
    if (!el) return;
    const calm = reducedMotion;
    let rx: Spring = { x: 0, v: 0 }, ry: Spring = { x: 0, v: 0 };
    let tx = 0, ty = 0;
    let wx = 0, wy = 0;
    let grabbing = false;
    let armed: { kind: Handle; node: HTMLElement } | null = null;
    let pid = -1;
    let unreg: (() => void) | null = null;

    const handleOf = (t: Element | null): { kind: Handle; node: HTMLElement } | null => {
      const h = t ? t.closest<HTMLElement>('[data-book-handle]') : null;
      if (!h || !el.contains(h)) return null;
      const kind = h.dataset.bookHandle as Handle;
      if (kind === 'cover' && openRef.current) return null; // an open cover is never a handle
      return { kind, node: h };
    };
    const write = () => {
      if (Math.abs(rx.x - wx) < EPS && Math.abs(ry.x - wy) < EPS) return;
      wx = rx.x; wy = ry.x;
      const rest = Math.abs(wx) < EPS && Math.abs(wy) < EPS;
      el.style.transform = rest ? '' : `perspective(1200px) rotateX(${wx.toFixed(3)}deg) rotateY(${wy.toFixed(3)}deg)`;
    };
    const tick = (dt: number) => {
      const p = grabbing ? PRESETS.snappy : PRESETS.bouncy;
      rx = stepSpring(rx, tx, dt, p);
      ry = stepSpring(ry, ty, dt, p);
      const done = !grabbing && settled(rx, tx) && settled(ry, ty);
      if (done) { rx = { x: tx, v: 0 }; ry = { x: ty, v: 0 }; }
      write();
      if (done && unreg) { unreg(); unreg = null; }
    };
    const wake = () => { if (!unreg) unreg = playLoop.register(tick); };

    // Native listener only to learn the pointerId for capture; never prevents default.
    let ptype = 'mouse';
    let cancelled = false;
    const onNativeDown = (e: PointerEvent) => { pid = e.pointerId; ptype = e.pointerType; cancelled = false; };
    // The browser took the gesture for native scroll (pan-y handle, vertical drag): never grab it.
    const onNativeCancel = () => { cancelled = true; if (grabbing) release(); };
    el.addEventListener('pointerdown', onNativeDown, { passive: true });
    el.addEventListener('pointercancel', onNativeCancel, { passive: true });

    const onDown = (e: PlayGesture) => { armed = handleOf(e.target); grabbing = false; };
    const onDragStart = (e: PlayGesture) => {
      if (!armed || calm) return;
      const s = pointerEngine.state;
      if (!mayGrab(ptype, getComputedStyle(armed.node).touchAction, e.x - s.downX, e.y - s.downY, cancelled)) return;
      grabbing = true;
      try { if (pid !== -1) armed.node.setPointerCapture(pid); } catch { /* pointer already gone */ }
      wake();
    };
    const onDrag = (e: PlayGesture) => {
      if (!grabbing) return;
      const s = pointerEngine.state;
      const t = tiltFor(e.x - s.downX, e.y - s.downY);
      tx = t.rx; ty = t.ry;
      wake();
    };
    const onFlick = (e: PlayGesture) => {
      if (!armed) return;
      const horizontal = Math.abs(e.vx) > Math.abs(e.vy);
      if (horizontal && armed.kind === 'cover' && e.vx < 0 && !openRef.current) { setRef.current(true); return; }
      if (horizontal && armed.kind === 'spine' && e.vx > 0 && openRef.current) { setRef.current(false); return; }
      if (!grabbing) return;
      ry = { x: ry.x, v: ry.v + clamp(e.vx * SPIN_GAIN, MAX_SPIN) };
      wake();
    };
    const release = () => {
      grabbing = false;
      tx = 0; ty = 0;
      wake();
    };
    const onDragEnd = () => { if (grabbing) release(); };
    // A tap on the closed cover always opens it (it is the only thing in front of the page);
    // reduced motion also lets a tap on the spine close it again.
    const onTap = (e: PlayGesture) => {
      const h = handleOf(e.target);
      if (h && h.kind === 'cover' && !openRef.current) setRef.current(true);
      else if (calm && h && h.kind === 'spine' && openRef.current) setRef.current(false);
    };
    const onUp = () => { armed = null; pid = -1; };
    const offs = [
      pointerEngine.on('down', onDown), pointerEngine.on('dragstart', onDragStart), pointerEngine.on('drag', onDrag),
      pointerEngine.on('flick', onFlick), pointerEngine.on('dragend', onDragEnd), pointerEngine.on('tap', onTap),
      pointerEngine.on('up', onUp),
    ];

    const dbg = process.env.NODE_ENV !== 'production' && typeof window !== 'undefined'
      ? (window as unknown as DebugHost).__playDebug : undefined;
    const peek = (): BookDebug => ({ rx: rx.x, ry: ry.x, grabbing, open: openRef.current, transform: el.style.transform });
    if (dbg) dbg.book = peek;

    return () => {
      offs.forEach((o) => o());
      el.removeEventListener('pointerdown', onNativeDown);
      el.removeEventListener('pointercancel', onNativeCancel);
      if (unreg) unreg();
      el.style.transform = '';
      if (dbg && dbg.book === peek) dbg.book = () => null;
    };
  }, [grabRef, reducedMotion]);
}
